import React, { createContext, useContext, useEffect, useState } from 'react';
import commonApi from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { CommonContext } from './CommonContext';
import { format } from 'date-fns';
import { scrollToTop } from '../components/common/scrollToTop';

export const GaintOFDOrdersContext = createContext();

const initialFilters = {
    searchBy: 'awb_no',
    searchValue: '',
    user_id: null,
    // order_status_id: [7],
    dateRange: {
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        endDate: new Date()
    },
    // tab_id: 7,
};

const tabOptions = [
    { label: "B2C", value: 1 },
    { label: "B2B", value: 2 }
];

export const GaintOFDOrdersContextProvider = ({ children }) => {
    const { user, setAlertMessage, setOpenAlert, setSeverity, globalSearch } = useContext(CommonContext);
    const navigate = useNavigate();

    const [filters, setFilters] = useState(initialFilters);
    const [activeTab, setActiveTab] = useState(tabOptions[0].value);
    const [selected, setSelected] = useState([]);
    const [allData, setAllData] = useState([]);
    const [allOrders, setAllOrders] = useState([]);
    const [firstLoading, setFirstLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [isLoading, setIsLoading] = useState(false);
    const [details, setDetails] = useState();
    const [isOpen, setIsOpen] = useState(false);
    const [openTracking, setOpenTracking] = useState(false);
    const [trackingInfo, setTrackingInfo] = useState(null);
    const [usersList, setUsersList] = useState([]);

    const handleToggleDrawer = () => {
        setIsOpen(!isOpen);
    };

    const fetchUsers = () => {
        const payload = { page: 1, per_page: 1000 };
        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        }
        commonApi.post('/users/list', payload)
            .then((response) => {
                if (response?.data?.data) {
                    setUsersList(response.data.data);
                }
            })
            .catch((error) => {
                console.error('Error fetching users:', error);
            });
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLoadApi = (customFilters = filters) => {
        const payload = {
            ...customFilters,
            service_type: activeTab,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        }

        delete payload.dateRange;
        setAllData([]);
        setAllOrders([]);
        setFirstLoading(false);
        const endpoint = activeTab === 2 ? "/orders/gaint-ofd-b2b-orders/list" : "/orders/gaint-ofd-orders/list";
        commonApi.post(`${endpoint}?page=${page + 1}&per_page=${rowsPerPage}`, payload)
            .then((response) => {
                setFirstLoading(true);
                if (response?.data?.data) {
                    setAllOrders(response?.data?.data);
                    setAllData(response?.data);
                }
            })
            .catch((error) => {
                setFirstLoading(true);
                console.error('Error fetching data:', error);
            });
    };

    const onTracking = (id) => {
        setOpenTracking(false);
        setTrackingInfo(null);
        setIsLoading(true);
        commonApi.post('/orders/tracking', { order_ids: id })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.s === 1) {
                    setOpenTracking(true);
                    setTrackingInfo(response?.data?.data)
                } else {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error');
                    setOpenAlert(true);
                    scrollToTop();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
                setAlertMessage(error?.response?.data?.msg);
                setSeverity('error');
                setOpenAlert(true);
                scrollToTop();
            });
    }

    const viewDetail = (id) => {
        setDetails(null);
        setIsLoading(true);
        commonApi.post('/orders/view', { id: id })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.s === 1) {
                    setDetails(response?.data?.data);
                    handleToggleDrawer();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    }

    useEffect(() => {
        const customFilters = { ...filters };
        if (globalSearch?.value) {
            customFilters.searchBy = 'awb_no';
            customFilters.searchValue = globalSearch?.value;
            customFilters.start_date = '';
            customFilters.end_date = '';
            customFilters.dateRange = { startDate: null, endDate: null };
            setFilters((prev) => ({ ...prev, searchBy: 'awb_no', searchValue: globalSearch?.value, dateRange: { startDate: null, endDate: null } }));
        }
        onLoadApi(customFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, activeTab, globalSearch?.searchTrigger]);

    const onManifestGen = (id = null) => {
        const orderIds = id ? [id] : selected;
        if (orderIds.length === 0) {
            setAlertMessage('Please select at least one order');
            setSeverity('error');
            setOpenAlert(true);
            return;
        }
        setIsLoading(true);
        const endpoint = activeTab === 2 ? "/orders/generate-gaint-b2b-manifest" : "/orders/generate-gaint-b2c-manifest";
        commonApi.post(endpoint, { "order_ids": orderIds.join(', ') })
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                } else {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('success');
                    if (!id) setSelected([]);
                    onLoadApi();
                    setTimeout(() => {
                        navigate('/download');
                    }, 3000);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    };

    const onExport = () => {
        let payload = {
            ...filters,
            service_type: activeTab,
            start_date: filters?.dateRange?.startDate
                ? format(new Date(filters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: filters?.dateRange?.endDate
                ? format(new Date(filters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };
        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        }
        if (selected.length > 0) {
            payload.order_ids = selected.join(', ')
        }

        delete payload.dateRange;
        setIsLoading(true);
        const endpoint = activeTab === 2 ? "/orders/gaint-ofd-b2b-orders/export" : "/orders/gaint-ofd-orders/export";
        commonApi.post(endpoint, payload)
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                } else {
                    navigate('/download');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
                setOpenAlert(true);
                setAlertMessage(error?.response?.data?.msg);
                setSeverity('error')
            });
    };

    const downLoadPod = (id) => {
        setIsLoading(true);
        commonApi.post('/orders/generate-epod', { "order_ids": id })
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                } else {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('success');
                    setTimeout(() => {
                        navigate('/download');
                    }, 3000);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    };

    const onCalling = (row) => {
        setIsLoading(true);
        const postReq = { order_id: row.id ?? '', awb_no: row.awb_no ?? '', customer_mobile: row.customer_mobile ?? '', ivr_mobile: user?.ivr_mobile ?? '' }
        commonApi.post('ivr/clickToCall', postReq)
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.s === 1) {
                    setOpenAlert(true);
                    setAlertMessage(response?.data?.msg);
                    setSeverity('success');
                } else {
                    setOpenAlert(true);
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setOpenAlert(true);
                setAlertMessage(error?.res?.data?.msg);
                setSeverity('error');
            });
    };

    const onRecording = (row) => {
        setIsLoading(true);
        commonApi.post('/ivr/list-recordings', { 'order_id': row?.id, 'customer_mobile': row?.customer_mobile, 'awb_no': row?.awb_no })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.s === 1) {
                    // Handle recording data if needed
                    setOpenAlert(true);
                    setAlertMessage('Recording feature not implemented for this module');
                    setSeverity('info');
                } else {
                    setOpenAlert(true);
                    setAlertMessage(response?.data?.msg || 'No recordings found');
                    setSeverity('error');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
                setOpenAlert(true);
                setAlertMessage(error?.response?.data?.msg || 'Error fetching recordings');
                setSeverity('error');
            });
    };

    const contextValue = {
        trackingInfo,
        openTracking,
        setOpenTracking,
        onTracking,
        isOpen,
        setIsOpen,
        handleToggleDrawer,
        viewDetail,
        details,
        initialFilters,
        allData,
        setAllData,
        allOrders,
        setAllOrders,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        firstLoading,
        setFirstLoading,
        isLoading,
        setIsLoading,
        onLoadApi,
        selected,
        setSelected,
        tabOptions,
        activeTab,
        setActiveTab,
        filters,
        setFilters,
        onExport,
        onManifestGen,
        downLoadPod,
        onCalling,
        onRecording,
        usersList
    };

    return (
        <GaintOFDOrdersContext.Provider value={contextValue}>
            {children}
        </GaintOFDOrdersContext.Provider>
    );
};
